import { useState, useRef, useCallback } from 'preact/hooks';
import type { I18nStrings, Attachment } from '@bitio-builder/shared';

const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface UploadedFile {
  url: string;
  file: File;
  isImage: boolean;
}

interface MessageInputProps {
  onSend: (message: string) => void;
  onFileUpload: (file: File) => Promise<Attachment | null>;
  disabled?: boolean;
  strings: I18nStrings;
}

const containerStyle: Record<string, string | number> = {
  padding: '16px',
  borderTop: '1px solid var(--bb-border, #E5E7EB)',
  background: 'white',
  flexShrink: 0,
};

const inputContainerStyle: Record<string, string | number> = {
  display: 'flex',
  gap: '8px',
  alignItems: 'flex-end',
};

const textareaStyle: Record<string, string | number> = {
  flex: 1,
  minHeight: '44px',
  maxHeight: '120px',
  padding: '8px 16px',
  border: '1px solid var(--bb-border, #E5E7EB)',
  borderRadius: '16px',
  resize: 'none',
  fontSize: '14px',
  lineHeight: 1.5,
  outline: 'none',
  fontFamily: 'inherit',
};

const buttonBaseStyle: Record<string, string | number> = {
  width: '44px',
  height: '44px',
  borderRadius: '9999px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  cursor: 'pointer',
};

const sendButtonStyle: Record<string, string | number> = {
  ...buttonBaseStyle,
  background: 'var(--bb-primary, #4D5D32)',
  color: 'white',
};

const imageButtonStyle: Record<string, string | number> = {
  ...buttonBaseStyle,
  background: 'var(--bb-background, #FAFAFA)',
  color: 'var(--bb-muted, #6B7280)',
};

const filePreviewContainerStyle: Record<string, string | number> = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  marginBottom: '8px',
};

const previewItemStyle: Record<string, string | number> = {
  position: 'relative',
  width: '60px',
  height: '60px',
};

const previewImageStyle: Record<string, string | number> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '4px',
};

const previewDocStyle: Record<string, string | number> = {
  width: '100%',
  height: '100%',
  borderRadius: '4px',
  background: 'var(--bb-background, #FAFAFA)',
  border: '1px solid var(--bb-border, #E5E7EB)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  color: 'var(--bb-muted, #6B7280)',
  overflow: 'hidden',
  padding: '4px',
};

const removeButtonStyle: Record<string, string | number> = {
  position: 'absolute',
  top: '-6px',
  right: '-6px',
  width: '20px',
  height: '20px',
  background: 'var(--bb-error, #DC2626)',
  color: 'white',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  cursor: 'pointer',
  border: 'none',
};

const SendIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const AttachIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style={{ width: '20px', height: '20px', flexShrink: 0 }}>
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);

const DocIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toUpperCase() || 'FILE';
}

export function MessageInput({
  onSend,
  onFileUpload,
  disabled,
  strings,
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = useCallback(() => {
    if (message.trim() || files.length > 0) {
      onSend(message.trim());
      setMessage('');
      setFiles([]);
    }
  }, [message, files, onSend]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleFileSelect = useCallback(
    async (e: Event) => {
      const input = e.target as HTMLInputElement;
      const selectedFiles = input.files;

      if (!selectedFiles || selectedFiles.length === 0) return;

      setIsUploading(true);

      for (const file of Array.from(selectedFiles)) {
        // Validate file type
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          console.warn(`File type not allowed: ${file.type}`);
          continue;
        }
        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          console.warn(`File too large: ${file.name}`);
          continue;
        }

        const isImage = file.type.startsWith('image/');
        const previewUrl = isImage ? URL.createObjectURL(file) : '';
        const serverUrl = await onFileUpload(file);

        if (serverUrl) {
          setFiles((prev) => [...prev, { url: previewUrl, file, isImage }]);
        } else if (isImage) {
          URL.revokeObjectURL(previewUrl);
        }
      }

      setIsUploading(false);
      input.value = '';
    },
    [onFileUpload]
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => {
      const removed = prev[index];
      if (removed.isImage && removed.url) {
        URL.revokeObjectURL(removed.url);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleInput = useCallback((e: Event) => {
    const textarea = e.target as HTMLTextAreaElement;
    setMessage(textarea.value);
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, []);

  const acceptTypes = '.jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt';

  return (
    <div style={containerStyle}>
      {files.length > 0 && (
        <div style={filePreviewContainerStyle}>
          {files.map((item, i) => (
            <div key={i} style={previewItemStyle}>
              {item.isImage ? (
                <img src={item.url} alt={`Preview ${i + 1}`} style={previewImageStyle} />
              ) : (
                <div style={previewDocStyle}>
                  <DocIcon />
                  <span style={{ marginTop: '2px', textAlign: 'center', wordBreak: 'break-all' }}>
                    {getFileExtension(item.file.name)}
                  </span>
                </div>
              )}
              <button style={removeButtonStyle} onClick={() => removeFile(i)}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={inputContainerStyle}>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptTypes}
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        <button
          style={imageButtonStyle}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
          title={strings.uploadFile}
        >
          <AttachIcon />
        </button>

        <textarea
          ref={textareaRef}
          style={textareaStyle}
          value={message}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={strings.placeholder}
          disabled={disabled || isUploading}
          rows={1}
        />

        <button
          style={{
            ...sendButtonStyle,
            opacity: disabled || isUploading || (!message.trim() && files.length === 0) ? 0.5 : 1,
          }}
          onClick={handleSubmit}
          disabled={disabled || isUploading || (!message.trim() && files.length === 0)}
          title={strings.send}
        >
          <SendIcon />
        </button>
      </div>
    </div>
  );
}
